<template lang="pug">
div.pb-6
  div.text-lg.font-medium.neutral700--text.mt-8 {{ $t('preliminary_questions_opt') }}
  div.text-sm.neutral500--text {{ $t('preliminary_questions_opt_desc') }}

  .pre-question
    div.d-flex.justify-center.mt-16(v-if="isFetching")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)      
      div.pt-3(v-if="questions.length > 0")
        v-card.round-xl.px-5.mt-3(v-for="question in questions" :key="question.custom_question_id" outlined height="86")
          div.d-flex.align-center.fill-height
            v-icon(color="neutral500") $yzi-dots-grid-02
            div.ml-4
              div.d-flex.align-center
                div.text-md.font-medium.neutral700--text {{ question.name }}
                v-chip.text-sm.warning700--text.ml-2(v-if="question.required" label small color="warning200") {{ $t('required') }}
              div.text-sm.neutral700--text.mt-1 Format: {{ getFormat(question.format) }}
            v-spacer
            div(v-if="service && !disabled")
              y-switch.mb-n2(v-model="question.enabled" @change="enableQuestion(question)")
            v-menu(v-if="!disabled" offset-y left rounded="lg" attach)
              template(#activator="{ on }")
                v-btn.ml-4(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list(dense width="120")
                v-list-item(@click="editQuestion(question)")
                  v-list-item-title.text-sm.basic800--text {{ $t('edit') }}
                v-divider
                v-list-item(@click="deleteQuestion(question)")
                  v-list-item-title.text-sm.danger500--text {{ $t('delete') }}

        v-card.round-xl.px-5.mt-3(v-if="!disabled" link outlined height="86" @click="openQuestionDialog = true")
          div.d-flex.align-center.fill-height
            v-avatar(color="neutral300" size="32")
              v-icon(color="neutral500" size="18") $yzi-plus
            div.text-md.font-semibold.neutral700--text.ml-4 {{ $t('add_question') }}

      v-btn.button-md.mt-6(v-else-if="!disabled" color="neutral700" outlined @click="openQuestionDialog = true") {{ $t('create_preliminary_question') }}

  .dialogs
    add-question-dialog(v-if="openQuestionDialog" :open.sync="openQuestionDialog" :question="selectedQuestion" @updated="reset" @added="addNewQuestion($event)" @closed="selectedQuestion = null")
    delete-question-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :question="selectedQuestion" :service="service" @deleted="reset")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AddQuestionDialog from '@/components/clinic/dialogs/AddQuestionDialog.vue'
import DeleteQuestionDialog from '@/components/clinic/dialogs/DeleteQuestionDialog.vue'

export default {
  name: 'ServicePreQuestions',
  components: { AddQuestionDialog, DeleteQuestionDialog },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isLoading: false,
      isFetching: false,
      openQuestionDialog: false,
      openDeleteDialog: false,
      questions: [],
      selectedQuestion: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      service: 'clinic/getService',
    }),
    questionData () {
      if (!this.service) {
        return {
          preliminary_questions: this.questions
        }
      }
      return null
    }
  },
  mounted () {
    // this.fetchServiceTypes()
    if (this.service) {
      this.setData(this.service)
    } else if (!this.disabled) {
      this.$watch('questionData', {
        deep: true,
        handler (val) {
          this.$store.commit('clinic/updateTempService', val)
        } 
      })
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setData (service) {
      this.questions = service.preliminary_questions
    },
    fetchPreQuestions () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/services/${this.service.id}`)
        .then(resp => {
          this.questions = resp.data.preliminary_questions
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    enableQuestion (question) {
      const data = { enabled: question.enabled }
      const endpoint = `/products/${this.service.product.id}/notes/${question.id}`
      this.$axios.patch(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'PATCH', question.id)
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    addNewQuestion (question) {
      if (this.selectedQuestion) {
        this.questions.splice(this.questions.findIndex(f => f.format === this.selectedQuestion.format && f.name === this.selectedQuestion.name), 1, question)
        this.selectedQuestion = null
      } else (
        this.questions.push(question)
      )
    },
    editQuestion (question) {
      this.selectedQuestion = question
      this.openQuestionDialog = true
    },
    deleteQuestion (question) {
      if (this.service) {
        this.selectedQuestion = question
        this.openDeleteDialog = true
      } else {
        this.questions = this.questions.filter(f => !(f.format === question.format && f.name === question.name))
      }
    },
    getFormat (format) {
      let formats = [
        { text: 'Text', value: 'short_text' },
        { text: 'Selection input', value: 'selection' },
        // { text: 'Phone number', value: 'phone' },
        { text: 'Number only', value: 'number' },
        // { text: 'Address', value: 'address' },
        { text: 'Text area field', value: 'long_text' }
      ]
      return formats.find(f => f.value === format) ? formats.find(f => f.value === format).text : ''
    },
    reset () {
      this.selectedQuestion = null
      this.fetchPreQuestions()
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'service',
        activity: 'Update service (preliminary questions)',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.inputText >>> input {
  margin-top: 3px;
  text-align: center;
}
</style>